import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import api from '../services/api';

const NotificationContext = createContext();

const notificationPriority = (item) => {
  const explicit = Number(item?.priority ?? item?.data?.priority)
  if (explicit >= 1 && explicit <= 6) return explicit
  const text = `${item?.type || ''} ${item?.title || ''} ${item?.message || ''}`.toLowerCase().replaceAll('_', ' ')
  if (/payment|money (received|sent)|estimate|quote|new message|job (booked|completed)|refund|dispute|chargeback|invoice paid|payroll completed/.test(text)) return 1
  if (/customer|order|invoice|merchant|qr|pos|bill payment|tax payment|wallet funding|booking update/.test(text)) return 2
  if (/verify|verification|required|awaiting|complete profile|complete registration|upload document/.test(text)) return 3
  if (/sign-in|login|password|device|security/.test(text)) return 4
  if (/upcoming|expiring|reminder|appointment/.test(text)) return 5
  return 6
}

const groupOrder = (item) => ({Today:1,Yesterday:2,Earlier:3}[item?.group || item?.data?.group] || 1)
const sortNotifications = (rows) => [...rows].sort((a,b) => groupOrder(a)-groupOrder(b) || Number(Boolean(b?.pinned ?? b?.data?.pinned))-Number(Boolean(a?.pinned ?? a?.data?.pinned)) || notificationPriority(a)-notificationPriority(b) || new Date(b.created_at || b.timestamp || 0)-new Date(a.created_at || a.timestamp || 0))

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const loadNotifications = useCallback(async (options = {}) => {
    const token =
      localStorage.getItem('token') ||
      localStorage.getItem('access_token');

    if (!token) {
      return [];
    }

    const response = await api.get('/notifications', {
      params: {
        platform: options.platform || 'website',
        locale: options.locale || localStorage.getItem('mojo_lang') || 'en',
      },
    });
    const rows = Array.isArray(response.data?.data) ? response.data.data : [];
    setNotifications(sortNotifications(rows));
    setUnreadCount(rows.filter((row) => !row.read_at).length);
    return rows;
  }, []);

  const loadUnreadCount = useCallback(async () => {
    const token =
      localStorage.getItem('token') ||
      localStorage.getItem('access_token');

    if (!token) {
      setUnreadCount(0);
      return 0;
    }

    const response = await api.get('/notifications/unread-count');
    const count = Number(response.data?.unread || 0);
    setUnreadCount(count);
    return count;
  }, []);

  useEffect(() => {
    loadNotifications().catch(() => {});
  }, [loadNotifications]);

  const addNotification = (notification) => {
    const newNotification = {
      id: Date.now(),
      ...notification,
      timestamp: new Date()
    };
    setNotifications(prev => sortNotifications([newNotification, ...prev]));
    setUnreadCount(prev => prev + 1);
    
    // Show toast notification
    switch (notification.type) {
      case 'success':
        toast.success(notification.message);
        break;
      case 'error':
        toast.error(notification.message);
        break;
      case 'warning':
        toast.warning(notification.message);
        break;
      case 'info':
        toast.info(notification.message);
        break;
      default:
        toast(notification.message);
    }
  };

  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const clearNotifications = () => {
    setNotifications([]);
  };

  const markAsRead = (id) => {
    setNotifications(prev => 
      prev.map(n => 
        n.id === id ? { ...n, read: true, read_at: n.read_at || new Date().toISOString() } : n
      )
    );
    setUnreadCount(prev => Math.max(prev - 1, 0));
    api.post(`/notifications/${id}/read`).catch(() => {});
  };

  const markAllRead = useCallback(async () => {
    setNotifications(prev =>
      prev.map(n => ({ ...n, read: true, read_at: n.read_at || new Date().toISOString() }))
    );
    setUnreadCount(0);
    try {
      await api.post('/notifications/read-all');
    } catch (error) {
      loadUnreadCount().catch(() => {});
    }
  }, [loadUnreadCount]);

  const value = {
    notifications,
    unreadCount,
    addNotification,
    removeNotification,
    clearNotifications,
    markAsRead,
    markAllRead,
    loadNotifications,
    loadUnreadCount
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
